import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    CircularProgress,
    Stack,
    Card,
    CardContent,
    Tooltip,
    Alert,
    Divider
} from '@mui/material';
import {
    Add as IconAdd,
    Remove as IconRemove,
    LockOpen as IconApertura,
    Lock as IconCierre,
    Category as IconCategory,
    Assessment as IconReport,
    AccountBalanceWallet as IconWallet,
    Block as IconAnnul,
    ReceiptLong as IconReceipt
} from '@mui/icons-material';
import moment from 'moment';
import { Link } from 'react-router-dom';

import {
    getsCajaChica,
    postAperturaCajaChica,
    postCierreCajaChica,
    getsMovimientos,
    postMovimiento,
    anularMovimiento,
    getsConceptosGasto,
    postConceptoGasto
} from '../../services/cajaChicaService';
import { getsTrabajador } from '../../services/trabajadorService';
import FiltroFilial from '../../components/common/FiltroFilial';
import { useFiltroFilial } from '../../hooks/useFiltroFilial';

import AperturaCajaDialog from '../../components/CajaChica/AperturaCajaDialog';
import CierreCajaDialog from '../../components/CajaChica/CierreCajaDialog';
import MovimientoCajaDialog from '../../components/CajaChica/MovimientoCajaDialog';
import ConceptosGastoDialog from '../../components/CajaChica/ConceptosGastoDialog';

const CajaChicaIndex = () => {
    const { filiales, idFilial, setIdFilial, bloqueado } = useFiltroFilial();

    const [cajas, setCajas] = useState([]);
    const [cajaActiva, setCajaActiva] = useState(null);
    const [movimientos, setMovimientos] = useState([]);
    const [conceptos, setConceptos] = useState([]);
    const [trabajadores, setTrabajadores] = useState([]);
    const [cargando, setCargando] = useState(false);

    // Modales
    const [openApertura, setOpenApertura] = useState(false);
    const [openCierre, setOpenCierre] = useState(false);
    const [openMovimiento, setOpenMovimiento] = useState(false);
    const [tipoOpMovimiento, setTipoOpMovimiento] = useState('EGRESO');
    const [openConceptos, setOpenConceptos] = useState(false);

    const cargarMaestros = useCallback(async () => {
        try {
            const [conRes, trabRes] = await Promise.all([
                getsConceptosGasto(),
                getsTrabajador ? getsTrabajador({ idFilial: idFilial || 0 }) : []
            ]);
            setConceptos(conRes || []);
            setTrabajadores(trabRes || []);
        } catch (err) {
            console.error('Error cargando maestros caja:', err);
        }
    }, [idFilial]);

    const cargarCajas = useCallback(async () => {
        setCargando(true);
        try {
            const data = await getsCajaChica({ idFilial: idFilial || 0 });
            setCajas(data || []);

            // Seleccionar por defecto la primera caja abierta de la filial, o la más reciente
            if (data && data.length > 0) {
                const abierta = data.find((c) => c.estado === 'ABIERTA');
                setCajaActiva(abierta || data[0]);
            } else {
                setCajaActiva(null);
            }
        } catch (err) {
            console.error('Error cargando cajas chicas:', err);
        } finally {
            setCargando(false);
        }
    }, [idFilial]);

    const cargarMovimientos = useCallback(async (idCaja) => {
        if (!idCaja) {
            setMovimientos([]);
            return;
        }
        try {
            const data = await getsMovimientos({ idCajaChica: idCaja });
            setMovimientos(data || []);
        } catch (err) {
            console.error('Error cargando movimientos caja:', err);
        }
    }, []);

    useEffect(() => {
        cargarMaestros();
    }, [cargarMaestros]);

    useEffect(() => {
        cargarCajas();
    }, [cargarCajas]);

    useEffect(() => {
        if (cajaActiva?.id) {
            cargarMovimientos(cajaActiva.id);
        } else {
            setMovimientos([]);
        }
    }, [cajaActiva, cargarMovimientos]);

    const handleAperturar = async (model) => {
        try {
            await postAperturaCajaChica(model);
            setOpenApertura(false);
            cargarCajas();
        } catch (err) {
            alert('Error al aperturar caja: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleCerrar = async (idCaja, obs) => {
        try {
            await postCierreCajaChica(idCaja, obs);
            setOpenCierre(false);
            cargarCajas();
        } catch (err) {
            alert('Error al cerrar caja: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleGuardarMovimiento = async (model) => {
        try {
            await postMovimiento(model);
            setOpenMovimiento(false);
            cargarCajas();
            if (cajaActiva?.id) cargarMovimientos(cajaActiva.id);
        } catch (err) {
            alert('Error al registrar movimiento: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleAnularMovimiento = async (idMov) => {
        const motivo = prompt('Ingrese el motivo de anulación del movimiento de caja chica:');
        if (motivo) {
            try {
                await anularMovimiento(idMov, motivo);
                cargarCajas();
                if (cajaActiva?.id) cargarMovimientos(cajaActiva.id);
            } catch (err) {
                alert('Error al anular movimiento: ' + (err.response?.data?.message || err.message));
            }
        }
    };

    const handleCrearConcepto = async (desc, abr) => {
        try {
            await postConceptoGasto(desc, abr);
            cargarMaestros();
        } catch (err) {
            alert('Error al crear concepto: ' + (err.response?.data?.message || err.message));
        }
    };

    const hayCajaAbierta = cajas.some((c) => c.estado === 'ABIERTA');

    return (
        <Box sx={{ p: 3 }}>
            {/* Cabecera */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1f2937', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconWallet color="primary" /> Gestión de Caja Chica
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Control de fondos fijos, aperturas, cierres, arqueos y rendiciones de gastos menores por filial.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<IconCategory />}
                        onClick={() => setOpenConceptos(true)}
                        sx={{ textTransform: 'none' }}
                    >
                        Conceptos de Gasto
                    </Button>
                    <Button
                        component={Link}
                        to={`/cajachica/reportes${cajaActiva ? `?idCajaChica=${cajaActiva.id}` : ''}`}
                        variant="outlined"
                        color="primary"
                        startIcon={<IconReport />}
                        sx={{ textTransform: 'none' }}
                    >
                        Arqueo y Reportes
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<IconApertura />}
                        disabled={hayCajaAbierta}
                        onClick={() => setOpenApertura(true)}
                        sx={{ textTransform: 'none', fontWeight: 'bold' }}
                    >
                        Aperturar Caja
                    </Button>
                </Stack>
            </Stack>

            {/* Selector de Filial y Selector de Caja */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#ffffff' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <FiltroFilial
                            filiales={filiales}
                            idFilial={idFilial}
                            setIdFilial={setIdFilial}
                            bloqueado={bloqueado}
                        />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', py: 0.5 }}>
                            {cajas.map((c) => (
                                <Chip
                                    key={c.id}
                                    label={`${c.codigo} (${c.estado}) - S/ ${Number(c.saldoDisponible).toFixed(2)}`}
                                    color={c.id === cajaActiva?.id ? 'primary' : 'default'}
                                    variant={c.id === cajaActiva?.id ? 'filled' : 'outlined'}
                                    onClick={() => setCajaActiva(c)}
                                    sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                                />
                            ))}
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Panel de la Caja Activa */}
            {cajaActiva ? (
                <>
                    <Card variant="outlined" sx={{ mb: 3, bgcolor: '#ffffff' }}>
                        <CardContent>
                            <Grid container spacing={3} alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                                        SALDO DISPONIBLE EN CAJA
                                    </Typography>
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: cajaActiva.saldoDisponible > 50 ? '#15803d' : '#dc2626',
                                            my: 0.5
                                        }}
                                    >
                                        S/ {Number(cajaActiva.saldoDisponible).toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Fondo Fijo Asignado: <b>S/ {Number(cajaActiva.montoFondoFijo).toFixed(2)}</b>
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1f2937' }}>
                                        {cajaActiva.nombre}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <b>Sede:</b> {cajaActiva.filial} | <b>Responsable:</b> {cajaActiva.responsable}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <b>Apertura:</b> {moment(cajaActiva.fechaApertura).format('DD/MM/YYYY HH:mm')}
                                    </Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <Chip
                                            label={cajaActiva.estado === 'ABIERTA' ? 'CAJA ABIERTA' : 'CAJA CERRADA'}
                                            color={cajaActiva.estado === 'ABIERTA' ? 'success' : 'default'}
                                            size="small"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={4} sx={{ textAlign: { md: 'right', xs: 'left' } }}>
                                    <Stack direction="column" spacing={1} sx={{ display: 'inline-flex', width: '100%', maxWidth: 220 }}>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            startIcon={<IconRemove />}
                                            disabled={cajaActiva.estado !== 'ABIERTA'}
                                            onClick={() => {
                                                setTipoOpMovimiento('EGRESO');
                                                setOpenMovimiento(true);
                                            }}
                                            sx={{ textTransform: 'none', fontWeight: 'bold' }}
                                        >
                                            Registrar Gasto / Egreso
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="success"
                                            startIcon={<IconAdd />}
                                            disabled={cajaActiva.estado !== 'ABIERTA'}
                                            onClick={() => {
                                                setTipoOpMovimiento('INGRESO');
                                                setOpenMovimiento(true);
                                            }}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Registrar Reembolso
                                        </Button>
                                        {cajaActiva.estado === 'ABIERTA' && (
                                            <Button
                                                variant="outlined"
                                                color="warning"
                                                startIcon={<IconCierre />}
                                                onClick={() => setOpenCierre(true)}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Cierre y Arqueo
                                            </Button>
                                        )}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Tabla de Movimientos */}
                    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1f2937' }}>
                                Movimientos de la Caja ({movimientos.length})
                            </Typography>
                        </Box>
                        <TableContainer sx={{ maxHeight: 550 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>N° Op.</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Concepto / Detalle</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Comprobante</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Proveedor / Rendidor</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Egreso (S/)</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Ingreso (S/)</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Saldo (S/)</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Estado</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Acción</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {movimientos.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                                No hay movimientos registrados en esta caja chica.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        movimientos.map((m) => (
                                            <TableRow key={m.id} hover>
                                                <TableCell sx={{ fontFamily: 'monospace' }}>#{m.numeroOperacion}</TableCell>
                                                <TableCell>{moment(m.fechaMovimiento).format('DD/MM/YYYY HH:mm')}</TableCell>
                                                <TableCell>{m.tipoMovimiento}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.conceptoGasto}</Typography>
                                                    {m.conceptoGastoCatalogo && m.conceptoGastoCatalogo !== m.conceptoGasto && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {m.conceptoGastoCatalogo}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {m.serieNumeroComprobante ? `${m.tipoComprobante || ''} ${m.serieNumeroComprobante}` : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {m.razonSocialProveedor || m.nombreRendidor || '-'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#dc2626', fontWeight: m.montoEgreso > 0 ? 'bold' : 'normal' }}>
                                                    {m.montoEgreso > 0 ? Number(m.montoEgreso).toFixed(2) : '-'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#16a34a', fontWeight: m.montoIngreso > 0 ? 'bold' : 'normal' }}>
                                                    {m.montoIngreso > 0 ? Number(m.montoIngreso).toFixed(2) : '-'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                    S/ {Number(m.saldoResultante).toFixed(2)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={m.estado}
                                                        color={m.estado === 'APROBADO' ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    {m.estado === 'APROBADO' && cajaActiva.estado === 'ABIERTA' && m.idTipoMovimiento !== 351 && (
                                                        <Tooltip title="Anular Movimiento">
                                                            <IconButton size="small" color="default" onClick={() => handleAnularMovimiento(m.id)}>
                                                                <IconAnnul fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            ) : (
                <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: '#fafafa' }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        No hay ninguna Caja Chica registrada en esta filial.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Aperture una nueva caja con el botón superior para comenzar a registrar movimientos y gastos menores.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<IconApertura />}
                        onClick={() => setOpenApertura(true)}
                    >
                        Aperturar Caja Chica Ahora
                    </Button>
                </Paper>
            )}

            {/* Modales */}
            <AperturaCajaDialog
                open={openApertura}
                onClose={() => setOpenApertura(false)}
                onSave={handleAperturar}
                filiales={filiales}
                idFilialDefault={idFilial}
                trabajadores={trabajadores}
            />

            <CierreCajaDialog
                open={openCierre}
                onClose={() => setOpenCierre(false)}
                onConfirm={handleCerrar}
                caja={cajaActiva}
            />

            <MovimientoCajaDialog
                open={openMovimiento}
                onClose={() => setOpenMovimiento(false)}
                onSave={handleGuardarMovimiento}
                caja={cajaActiva}
                conceptos={conceptos}
                tipoOperacionDefault={tipoOpMovimiento}
            />

            <ConceptosGastoDialog
                open={openConceptos}
                onClose={() => setOpenConceptos(false)}
                conceptos={conceptos}
                onCrearConcepto={handleCrearConcepto}
            />
        </Box>
    );
};

export default CajaChicaIndex;
